import { Fragment, Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut, Menu, Search, Settings, User, Users } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/store/auth';
import { useUi } from '@/store/ui';
import { useBreadcrumbStore } from '@/store/breadcrumb';
import { logout } from '@/api/auth';
import { SwitchAccountDialog } from '@/routes/SwitchAccountDialog';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { Avatar } from '@/components/ui/Avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/Sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/Breadcrumb';
import { profilePicPath } from '@/lib/profilePic';
import { navForRole, NAV_TONE_ICON, type NavItem } from '@/routes/nav';
import { RouteFallback } from '@/routes/RouteFallback';
import { CommandPalette } from '@/routes/CommandPalette';
import { Toaster } from '@/components/ui/Sonner';
import { toast } from 'sonner';
import { listenForPush, onPushEnabled, syncPushToken } from '@/lib/push';
import { notificationCta } from '@/pages/admin/notifications/screenRoute';

function Brand({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  /** When provided, the logo acts as a sidebar toggle (desktop). When omitted,
   *  the whole bar is a link to /admin (mobile sheet). */
  onToggle?: () => void;
}) {
  if (onToggle) {
    return (
      <div className="flex h-16 items-center gap-3 border-b border-app-border px-4">
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="-m-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition hover:bg-app-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src="/icons/partapp-icon.png"
            alt="PartApp"
            width={32}
            height={32}
            className="rounded-md"
          />
        </button>
        {!collapsed && (
          <Link to="/admin" className="text-lg font-bold tracking-tight hover:text-primary">
            PartApp
          </Link>
        )}
      </div>
    );
  }
  return (
    <Link to="/admin" className="flex h-16 items-center gap-3 border-b border-app-border px-4">
      <img src="/icons/partapp-icon.png" alt="" width={32} height={32} className="rounded-md" />
      {!collapsed && <span className="text-lg font-bold tracking-tight">PartApp</span>}
    </Link>
  );
}

function NavList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const role = useAuth((s) => s.profile?.role);
  const items = navForRole(role);
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavItemLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
        </li>
      ))}
    </ul>
  );
}

function NavItemLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const toneClass = item.tone ? NAV_TONE_ICON[item.tone] : '';
  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/admin'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/15 text-primary'
            // Hover lifts toward white. bg-app-surface-2 would have been *lighter*
            // than the page but is now lighter than the shaded sidebar too, so it
            // still reads as a lift — but state it explicitly against the surface.
            : 'text-app-text/80 hover:bg-app-surface/70 hover:text-app-text dark:hover:bg-app-surface-2',
          collapsed && 'justify-center px-2',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('h-4 w-4 shrink-0', !isActive && toneClass)} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function pathLabel(segment: string): string {
  // Numeric or short ids → display as #id; words → Title Case.
  if (/^\d+$/.test(segment) || /^[a-f0-9-]{6,}$/i.test(segment)) return `#${segment}`;
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Crumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean); // e.g. ['admin','students','42','edit']
  const overrides = useBreadcrumbStore((s) => s.labels);
  if (segments.length === 0) return null;

  // Build cumulative paths. A page can register a per-path override via
  // useCrumbLabel(path, name) — used to render real names instead of "#15".
  const trail = segments.map((seg, i) => {
    const to = '/' + segments.slice(0, i + 1).join('/');
    const fallback = i === 0 ? 'Admin' : pathLabel(seg);
    return { label: overrides[to] ?? fallback, to };
  });

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.map((c, i) => {
          const last = i === trail.length - 1;
          return (
            <Fragment key={c.to}>
              <BreadcrumbItem>
                {last ? (
                  <BreadcrumbPage>{c.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={c.to}>{c.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!last && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const navigate = useNavigate();
  const profile = useAuth((s) => s.profile);
  const clear = useAuth((s) => s.clear);
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const toggleCollapsed = useUi((s) => s.toggleSidebar);
  const setCommandOpen = useUi((s) => s.setCommandOpen);
  const role = profile?.role;
  // Bumped when push is switched on mid-session so the listener below
  // re-subscribes instead of waiting for the next full page load.
  const [pushEpoch, setPushEpoch] = useState(0);

  async function onLogout() {
    await logout();
    clear();
    navigate('/login', { replace: true });
  }

  // FCM rotates tokens (reinstall, cleared site data, long inactivity), so the
  // stored one is refreshed on every load once permission is already granted.
  // No-op — and never a prompt — when the user hasn't opted in.
  useEffect(() => {
    void syncPushToken();
  }, []);

  // A push that lands while the tab is focused never reaches the service
  // worker, so it is surfaced as a toast instead.
  useEffect(() => onPushEnabled(() => setPushEpoch((n) => n + 1)), []);

  useEffect(() => {
    let stop: (() => void) | undefined;
    let cancelled = false;
    void listenForPush(({ title, body, data }) => {
      const route = notificationCta(data.screen, data.entity_id, data.entity_type, role, data);
      toast(title, {
        description: body || undefined,
        action: route ? { label: 'View', onClick: () => navigate(route.href) } : undefined,
      });
    }).then((unsubscribe) => {
      // Subscription resolves asynchronously; if the effect was torn down while
      // it was in flight, drop it immediately rather than leaking a listener.
      if (cancelled) unsubscribe();
      else stop = unsubscribe;
    });
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [navigate, role, pushEpoch]);

  // Clicking a background notification focuses this tab; the worker posts the
  // payload across so the app can route using the same mapping the
  // notifications list uses.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    function onMessage(event: MessageEvent) {
      if (event.data?.type !== 'notification-click') return;
      const data = event.data.data ?? {};
      const route = notificationCta(data.screen, data.entity_id, data.entity_type, role, data);
      navigate(route?.href ?? '/admin/notifications');
    }
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate, role]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex min-h-screen bg-app-bg text-app-text">
        {/* Desktop sidebar */}
        <aside
          className={cn(
            'sticky top-0 z-30 hidden h-screen shrink-0 flex-col border-r border-app-border bg-app-sidebar lg:flex',
            collapsed ? 'w-[72px]' : 'w-64',
          )}
        >
          <Brand collapsed={collapsed} onToggle={toggleCollapsed} />
          <nav className="flex-1 overflow-y-auto p-3">
            <NavList collapsed={collapsed} />
          </nav>
          <div className="border-t border-app-border p-2">
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="justify-center"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              {!collapsed && <span className="ml-1 text-xs text-app-hint">Collapse</span>}
            </Button>
          </div>
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-app-border bg-app-bg/80 px-4 backdrop-blur lg:px-6">
            {/* Mobile sidebar trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <SheetHeader>
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                </SheetHeader>
                <Brand collapsed={false} />
                <nav className="p-3">
                  <NavList collapsed={false} onNavigate={() => setMobileOpen(false)} />
                </nav>
              </SheetContent>
            </Sheet>

            <div className="hidden min-w-0 flex-1 lg:block">
              <Crumbs />
            </div>
            <div className="flex-1 lg:hidden" />

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCommandOpen(true)}
              className="hidden gap-2 text-app-hint sm:inline-flex"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Search…</span>
              <kbd className="ml-2 hidden rounded bg-app-surface-2 px-1.5 py-0.5 text-[10px] font-medium md:inline-flex">
                ⌘K
              </kbd>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCommandOpen(true)}
              className="sm:hidden"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </Button>

            <ThemeToggle />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full p-0.5 transition hover:bg-app-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Account menu"
                >
                  <Avatar
                    size={32}
                    name={profile?.name}
                    apiPath={profilePicPath(
                      profile?.role === 'manager' || profile?.role === 'trainer'
                        ? profile.role
                        : 'admin',
                      profile?.id,
                      profile?.profile_pic,
                    )}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{profile?.name ?? 'Admin'}</span>
                    <span className="truncate text-xs font-normal text-app-hint">
                      {profile?.academy_name ?? 'Academy'}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate('/admin/profile')}>
                  <User className="h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                {profile?.role === 'admin' && (
                  <DropdownMenuItem onSelect={() => navigate('/admin/membership')}>
                    <Settings className="h-4 w-4" />
                    Membership
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setSwitchOpen(true)}>
                  <Users className="h-4 w-4" />
                  Switch account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <main className="flex-1 px-4 py-6 lg:px-8">
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>

        <SwitchAccountDialog open={switchOpen} onOpenChange={setSwitchOpen} />
        <CommandPalette />
        <Toaster position="top-right" />
      </div>
    </TooltipProvider>
  );
}
